import { motion } from "framer-motion";

const CHAPTERS = [
  {
    num: "01",
    title: "Wooden ghani extraction",
    body: "Our oils are mechanically pressed in a traditional wooden ghani. Extraction stays below 45°C — no solvent extraction, no industrial refining.",
  },
  {
    num: "02",
    title: "Small batches",
    body: "We press in small batches, made for freshness rather than volume. What leaves the press gets bottled, not warehoused.",
  },
  {
    num: "03",
    title: "Minimal processing",
    body: "Naturally settled and gently filtered. No refining, no deodorising, no preservatives — one ingredient on the label because only one goes in.",
  },
  {
    num: "04",
    title: "Transparent quality",
    body: "Every oil is independently lab-tested — and the reports are published for you to download and check yourself.",
  },
  {
    num: "05",
    title: "Careful packaging",
    body: "Bottled in glass and food-grade plastic — sealed tight and packed carefully so it reaches your kitchen exactly as it left the press.",
  },
  {
    num: "06",
    title: "Made for Indian cooking",
    body: "Tadka, pickles, deep-frying, everyday sabzi — oils chosen and made for how India actually cooks.",
  },
];

export const Manifesto = () => {
  return (
    <section
      id="why"
      data-testid="manifesto-section"
      className="relative bg-ink grain py-28 md:py-40 overflow-hidden"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-xs tracking-[0.4em] uppercase text-gold mb-4"
        >
          02 — Why PhudG
        </motion.p>
        <div className="overflow-hidden mb-20 md:mb-28">
          <motion.h2
            initial={{ y: "100%" }}
            whileInView={{ y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-5xl md:text-7xl text-cream tracking-tighter"
          >
            Here's how <span className="italic text-gold">we make it.</span>
          </motion.h2>
        </div>

        <div className="grid md:grid-cols-12 gap-14 md:gap-10 items-start">
          <motion.div className="md:col-span-5 md:sticky md:top-32 self-start">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-gold/25"
            >
              <img
                src="/images/story.jpg"
                alt="A farmer standing in a mustard field at sunrise"
                loading="lazy"
                className="w-full aspect-[4/5] object-cover"
              />
            </motion.div>
            <p className="mt-6 text-xs tracking-[0.3em] uppercase text-cream/40 text-center">
              Wood-pressed · Small batches · Below 45°C
            </p>
          </motion.div>

          <div className="md:col-span-7 flex flex-col">
            {CHAPTERS.map((c, i) => (
              <motion.div
                key={c.num}
                data-testid={`manifesto-chapter-${c.num}`}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                className="group flex gap-8 border-t border-white/10 py-8 md:py-10 last:border-b"
              >
                <span className="font-serif text-5xl md:text-6xl text-gold/25 group-hover:text-gold/60 transition-colors duration-500 leading-none shrink-0 w-20">
                  {c.num}
                </span>
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl text-cream mb-3">{c.title}</h3>
                  <p className="text-sm md:text-base font-light text-cream/60 leading-relaxed max-w-xl">
                    {c.body}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
